import { Box, IconButton, Typography, Modal, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { PieChart, Pie, Cell, ResponsiveContainer, Label, Tooltip } from 'recharts';
import menuIcon from 'packages/novous_web/src/assets/icons/new-menu.svg';
import OpenInFullIcon from '../../../../assets/icons/Expand.svg';
import EditIcon from '../../../../assets/icons/edit.svg';
import CollapseIcon from '../../../../assets/icons/CollapseIcon.svg';
import RestartAltIcon from '../../../../assets/icons/Reset.svg';
import DriverScoringModal from 'packages/novous_web/src/pages/Dashboard/Directories/Drivers/components/DriverScoringModal';
import EditDataModal from './EditDataModal';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

const CommonGaugeMeterChart = ({
  containerClassName,
  title,
  value,
  infoIcon,
  onCardDataUpdate,
  chartId,
  customGaugeLevels,
}: {
  containerClassName: string;
  title: string;
  value: number;
  infoIcon?: React.ReactNode;
  onCardDataUpdate?: (chartId?: number, startDate?: string, endDate?: string) => void;
  chartId?: number;
  customGaugeLevels?: Array<{
    label: string;
    range: string;
    min: number;
    max: number;
    color: string;
  }>;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpandedModalOpen, setIsExpandedModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleInfoClick = () => {
    // Only open modal for Safety Score, not for Driver Productivity Index
    if (title !== 'Driver Productivity Index') {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', title);
    setIsExpandedModalOpen(true);
    handleMenuClose();
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', title);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    console.log('Applied edit data:', data);
    // Call the onCardDataUpdate callback to refresh the chart data
    if (onCardDataUpdate) {
      onCardDataUpdate(chartId, data.startDate, data.endDate);
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', title);
    handleMenuClose();
    // Reset to last 7 days
    if (onCardDataUpdate) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onCardDataUpdate(chartId, startDate, endDate);
    }
  };

  const handleCloseExpandedModal = () => {
    setIsExpandedModalOpen(false);
  };
  const theme = useTheme();

  const defaultGaugeLevels = [
    {
      label: 'Low',
      range: '00-70',
      min: 0,
      max: 70,
      color: '#C83333',
    },
    {
      label: 'Moderate',
      range: '70-90',
      min: 70,
      max: 90,
      color: '#F2994A',
    },
    {
      label: 'High',
      range: '90-100',
      min: 90,
      max: 100,
      color: '#16A34A',
    },
  ];

  // Use custom gauge levels if provided, otherwise use default
  const gaugeLevels = customGaugeLevels || defaultGaugeLevels;
  const filledValue = value > 100 ? 100 : value < 0 ? 0 : value;

  const data = [
    { name: 'filled', value: filledValue },
    { name: 'remaining', value: 100 - filledValue },
  ];

  const getColor = (val: number) => {
    const level = gaugeLevels.find((lvl) => val > lvl.min && val <= lvl.max);
    return level ? level.color : '#E5E7EB';
  };

  const COLORS = [getColor(filledValue), '#E5E7EB'];

  const startAngle = 225;
  const endAngle = -45;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload[0]) {
      // Only show tooltip for the "filled" section
      if (payload[0].name === 'filled') {
        const level = gaugeLevels.find((lvl) => filledValue > lvl.min && filledValue <= lvl.max);
        return (
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              padding: '8px 12px',
              borderRadius: '4px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {level ? level.label : 'N/A'}
            </Typography>
          </Box>
        );
      }
    }
    return null;
  };

  return (
    <Box className="w-full h-full flex flex-col border rounded-md p-4 gap-2 items-start bg-white">
      <Box className="flex items-center justify-between w-full">
        <Box className="flex items-center gap-2">
          <Typography
            variant="h6"
            fontSize={18}
            sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600 })}
          >
            {title}
          </Typography>
          {infoIcon && (
            <Box
              onClick={() => {
                if (title === 'Safety Score') {
                  handleInfoClick();
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: title === 'Safety Score' ? 'pointer' : 'default',
                width: 16,
                height: 16,
                '&:hover': {
                  opacity: title === 'Safety Score' ? 0.8 : 1,
                },
              }}
            >
              {infoIcon}
            </Box>
          )}
        </Box>
        <Box className="flex items-center gap-1 cursor-pointer">
          <IconButton
            sx={{
              p: 0,
              width: '24px !important',
              height: '24px !important',
              minWidth: '24px !important',
              minHeight: '24px !important',
              maxWidth: '24px !important',
              maxHeight: '24px !important',
              flexShrink: 0,
              display: 'flex !important',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={handleMenuClick}
          >
            <img
              src={menuIcon}
              alt="Menu"
              width="24"
              height="24"
              style={{
                display: 'block',
                width: '24px',
                height: '24px',
                minWidth: '24px',
                minHeight: '24px',
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
      </Menu>

      {value !== null && value !== undefined && value > 0 ? (
        <Box className="flex sm:flex-row w-full justify-center items-center flex-col gap-4">
          <Box
            className="flex-1 flex justify-center items-center mt-8"
            sx={{
              minWidth: 0,
              minHeight: 0,
              aspectRatio: '1 / 1',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '400px',
              alignSelf: 'flex-start',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  startAngle={startAngle}
                  endAngle={endAngle}
                  innerRadius="65%"
                  outerRadius="85%"
                  paddingAngle={0}
                  stroke="none"
                  labelLine={false}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      const cx = (viewBox as any)?.cx ?? 125;
                      const cy = (viewBox as any)?.cy ?? 125;

                      return (
                        <>
                          {/* Center Value */}
                          <text
                            x={cx}
                            y={cy}
                            textAnchor="middle"
                            dominantBaseline="central"
                            style={{ fill: '#000' }}
                          >
                            <tspan x={cx} dy="-0.5em" fontSize="32" fontWeight="bold">
                              {Math.round(Number(filledValue))}
                            </tspan>
                          </text>
                        </>
                      );
                    }}
                  />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <Box className="flex flex-1 flex-col items-center gap-3 flex-wrap" sx={{ opacity: 1 }}>
            {gaugeLevels.map((level, idx) => (
              <Box key={idx} className="flex items-center gap-2" sx={{ gap: '12px' }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: level.color,
                    opacity: 1,
                    transform: 'rotate(0deg)',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1B1B1B',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    minWidth: '70px',
                  }}
                >
                  {level.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#000000',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    minWidth: '50px',
                  }}
                >
                  {level.range}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
        </Box>
      )}

      {/* Expanded Gauge Modal */}
      <Modal
        open={isExpandedModalOpen}
        onClose={handleCloseExpandedModal}
        aria-labelledby="expanded-gauge-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            maxWidth: 600,
            maxHeight: { xs: '85vh', sm: '90vh', md: '95vh' },
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: 24,
            p: { xs: 2, sm: 2.5, md: 3 },
            outline: 'none',
            overflow: 'auto',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: { xs: '18px', sm: '24px', md: '28px' },
                  lineHeight: { xs: '27px', sm: '36px', md: '42px' },
                  letterSpacing: '0%',
                  color: '#1B1B1B',
                }}
              >
                {title}
              </Typography>
              {infoIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>{infoIcon}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Collapse Button */}
              <Box
                onClick={handleCloseExpandedModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Expanded Gauge */}
          {value !== null && value !== undefined && value > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                mt: { xs: 2, sm: 2.5, md: 3 },
              }}
            >
              {/* Gauge Chart */}
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 300, sm: 400, md: 450 },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="value"
                      startAngle={startAngle}
                      endAngle={endAngle}
                      innerRadius="65%"
                      outerRadius="85%"
                      paddingAngle={0}
                      stroke="none"
                      labelLine={false}
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                      <Label
                        content={({ viewBox }) => {
                          const cx = (viewBox as any)?.cx ?? 125;
                          const cy = (viewBox as any)?.cy ?? 125;

                          return (
                            <>
                              {/* Center Value */}
                              <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dominantBaseline="central"
                                style={{ fill: '#000' }}
                              >
                                <tspan x={cx} dy="-0.5em" fontSize="48" fontWeight="bold">
                                  {Math.round(Number(filledValue))}
                                </tspan>
                              </text>
                            </>
                          );
                        }}
                      />
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              {/* Legend */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                {gaugeLevels.map((level, idx) => (
                  <Box key={idx} className="flex items-center gap-2" sx={{ gap: '12px' }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: level.color,
                        opacity: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#1B1B1B',
                        fontWeight: 500,
                        fontSize: '15px',
                        lineHeight: '22px',
                        letterSpacing: '0%',
                        flex: 1,
                      }}
                    >
                      {level.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#000000',
                        fontWeight: 600,
                        fontSize: '15px',
                        lineHeight: '22px',
                        letterSpacing: '0%',
                        minWidth: '60px',
                        textAlign: 'right',
                      }}
                    >
                      {level.range}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                minHeight: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
            </Box>
          )}
        </Box>
      </Modal>

      {/* Edit Data Modal - Removed for Safety Score */}

      <DriverScoringModal open={isModalOpen} onClose={handleCloseModal} />
    </Box>
  );
};

export default CommonGaugeMeterChart;
